/**
 * MATAFOME — API DE PEDIDOS, KITCHEN KDS, FIDELIDADE, CUPONS E RELATÓRIOS
 */

import { safeApiFetch, getAuthHeaders } from './core.api';

export async function fetchOrders() {
    const authHeaders = getAuthHeaders();
    const ordersPromise = safeApiFetch('/api/orders', { headers: authHeaders });
    const queuePromise = safeApiFetch('/api/tables/kitchen-queue', { headers: authHeaders }).catch(() => null);

    const res = await ordersPromise;
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao carregar pedidos.');
    const regularOrders = data.orders || [];

    try {
        const queueRes = await queuePromise;
        if (queueRes.ok) {
            const queueData = await queueRes.json();
            const batches = (queueData.queue || []).map(b => ({
                id: `M-${b.tableNumber || ''} (${b.batchId ? b.batchId.slice(-4).toUpperCase() : ''})`,
                rawId: b.batchId,
                batchId: b.batchId,
                tableId: b.tableId,
                tableLabel: b.tableLabel || `Mesa ${b.tableNumber}`,
                tableNumber: b.tableNumber,
                comandaId: b.comandaId,
                type: 'dine_in_batch',
                isDineInBatch: true,
                source: 'dine_in',
                status: b.status === 'ready' ? 'delivered' : 'in_prep',
                rawStatus: b.status,
                items: b.items || [],
                created_at: b.createdAt,
                total: 0,
                subtotal: 0,
                customer_name: b.tableLabel || `Mesa ${b.tableNumber}`,
                customer_phone: '',
                payment_method: 'Salão',
                delivery_address: `Consumo no Salão • ${b.tableLabel || `Mesa ${b.tableNumber}`}`
            }));
            return [...regularOrders, ...batches];
        }
    } catch (e) {
        console.warn('[API] Não foi possível obter fila da cozinha das mesas:', e);
    }

    return regularOrders;
}

export async function fetchKitchenQueue() {
    const res = await safeApiFetch('/api/tables/kitchen-queue', { headers: getAuthHeaders() });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao obter fila da cozinha.');
    return data.queue || [];
}

export async function updateTableBatchStatus(tableId, batchId, status) {
    const res = await safeApiFetch(`/api/tables/${tableId}/production/${batchId}/status`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao atualizar status do lote de produção.');
    return data.batch;
}

export async function updateOrderStatus(orderId, status) {
    const res = await safeApiFetch(`/api/orders/${orderId}/status`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao atualizar pedido.');
    return data.order;
}

export async function updateOrderPaymentStatus(orderId, payment_status) {
    const res = await safeApiFetch(`/api/orders/${orderId}/payment-status`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ payment_status })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao atualizar pagamento do pedido.');
    return data.order;
}

// ----------------------------------------------------
// PROGRAMA DE FIDELIDADE & CUPONS
// ----------------------------------------------------

export async function fetchLoyaltyProgram() {
    const res = await safeApiFetch('/api/loyalty/program', { headers: getAuthHeaders() });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao obter programa de fidelidade.');
    return data.program;
}

export async function updateLoyaltyProgram(programData) {
    const res = await safeApiFetch('/api/loyalty/program', {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(programData)
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao atualizar programa de fidelidade.');
    return data.program;
}

export async function fetchCoupons() {
    const res = await safeApiFetch('/api/loyalty/coupons', { headers: getAuthHeaders() });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao carregar cupons.');
    return data.coupons || [];
}

export async function createCoupon(couponData) {
    const res = await safeApiFetch('/api/loyalty/coupons', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(couponData)
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao criar cupom.');
    return data.coupon;
}

export async function deleteCoupon(id) {
    const res = await safeApiFetch(`/api/loyalty/coupons/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao excluir cupom.');
    return data;
}

export async function fetchCustomerLoyalty() {
    const res = await safeApiFetch('/api/loyalty/customers', { headers: getAuthHeaders() });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao carregar ranking de clientes fiéis.');
    return data.customers || [];
}

// ----------------------------------------------------
// WEBSOCKET REALTIME (SOCKET.IO)
// ----------------------------------------------------

export async function fetchReportsOverview(period = 'all') {
    const res = await safeApiFetch(`/api/reports/overview?period=${period}`, {
        headers: getAuthHeaders()
    });
    if (!res.ok) throw new Error('Falha ao carregar visão geral de relatórios.');
    return res.json();
}

export async function fetchSalesByHour(period = 'all') {
    const res = await safeApiFetch(`/api/reports/sales-by-hour?period=${period}`, {
        headers: getAuthHeaders()
    });
    if (!res.ok) throw new Error('Falha ao carregar vendas por horário.');
    return res.json();
}

export async function fetchTopProducts(period = 'all') {
    const res = await safeApiFetch(`/api/reports/top-products?period=${period}`, {
        headers: getAuthHeaders()
    });
    if (!res.ok) throw new Error('Falha ao carregar top produtos.');
    return res.json();
}

export async function fetchPaymentMethods(period = 'all') {
    const res = await safeApiFetch(`/api/reports/payment-methods?period=${period}`, {
        headers: getAuthHeaders()
    });
    if (!res.ok) throw new Error('Falha ao carregar métodos de pagamento.');
    return res.json();
}

// ----------------------------------------------------
// FECHAMENTO DE CAIXA & GESTÃO DE TURNO
// ----------------------------------------------------
